import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  DatePicker,
  Table,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Divider,
  message,
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Tooltip,
} from "antd";
import { 
  PlusOutlined, 
  DownloadOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  DollarOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import './PhieuDat.css';

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PhieuDat = () => {
  // States
  const [phieuDatList, setPhieuDatList] = useState([]);
  const [kpiList, setKpiList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isKpiModalVisible, setIsKpiModalVisible] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [form] = Form.useForm();
  const [kpiForm] = Form.useForm();

  // Reference for the order details table
  const ordersFormListRef = useRef([]);

  // Initial fetch
  useEffect(() => {
    fetchPhieuDat();
    fetchKPI();
  }, []);

  // Fetch order data with loading state management
  const fetchPhieuDat = async (showMessage = false) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error("Vui lòng chọn khoảng thời gian");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get("https://doticom.vn:3000/api/LayTatCaPhieuDat", {
        params: {
          NgayBatDau: dateRange[0].format("YYYY-MM-DD"),
          NgayKetThuc: dateRange[1].format("YYYY-MM-DD"),
        },
      });

      if (response.data.success) {
        setPhieuDatList(response.data.data);
        if (showMessage) {
          message.success("Cập nhật dữ liệu phiếu đặt thành công");
        }
      } else {
        message.error("Lỗi khi tải dữ liệu: " + response.data.error);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Lỗi khi tải dữ liệu: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch KPI data with loading state management
  const fetchKPI = async (showMessage = false) => {
    setKpiLoading(true);
    try {
      const response = await axios.get("https://doticom.vn:3000/api/LayTatCaKPI");
      
      if (response.data.success) {
        setKpiList(response.data.data);
        if (showMessage) {
          message.success("Cập nhật dữ liệu KPI thành công");
        }
      } else {
        message.error("Lỗi khi tải dữ liệu KPI: " + response.data.error);
      }
    } catch (error) {
      console.error("Error fetching KPI data:", error);
      message.error("Lỗi khi tải dữ liệu KPI: " + error.message);
    } finally {
      setKpiLoading(false);
    }
  };

  // Refresh handlers
  const handleRefreshPhieuDat = () => {
    fetchPhieuDat(true);
  };

  const handleRefreshKPI = () => {
    fetchKPI(true);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setDatePickerOpen(false);
    }
  };

  // Handle date picker open state
  const handleDatePickerOpenChange = (open) => {
    setDatePickerOpen(open);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!phieuDatList || phieuDatList.length === 0) {
      message.warning("Không có dữ liệu để xuất");
      return;
    }

    // Prepare data for export
    const exportData = phieuDatList.map((item, index) => {
      const phieu = item.Phieu;
      const chiTiet = item.ChiTiet;
      
      // Calculate totals
      const tongSoLuong = chiTiet.reduce((sum, ct) => sum + ct.SoLuong, 0);
      const tongTien = chiTiet.reduce((sum, ct) => sum + ct.ThanhTien, 0);
      const tienCK = (tongTien * phieu.CK_PT / 100) + (phieu.CK_TM || 0);
      const thanhToan = tongTien - tienCK;
      
      return {
        "STT": index + 1,
        "ID Phiếu": phieu.IDPhieu,
        "Người Đặt": phieu.NguoiDat,
        "Người Nhận": phieu.NguoiNhan,
        "SĐT": phieu.SDT,
        "Khối": phieu.MaKhoi,
        "Nhóm": phieu.MaNhom,
        "Ngày Giao": dayjs(phieu.NgayGiao).format("DD/MM/YYYY"),
        "Tổng SL": tongSoLuong,
        "Tổng Tiền": tongTien,
        "CK (%)": phieu.CK_PT,
        "CK Nội Bộ": phieu.CK_PT_NB ? "Có" : "Không",
        "CK Tiền Mặt": phieu.CK_TM,
        "Tiền CK": tienCK,
        "Thanh Toán": thanhToan,
      };
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Phiếu Đặt");

    // Save to file
    const fileName = `PhieuDat_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Xuất Excel thành công");
  };

  // Show add order modal
  const showAddModal = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  // Show add KPI modal
  const showKpiModal = () => {
    kpiForm.resetFields();
    setIsKpiModalVisible(true);
  };

  // Handle modal cancel
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Handle KPI modal cancel
  const handleKpiCancel = () => {
    setIsKpiModalVisible(false);
  };

  // Calculate totals for order details
  const calculateOrderTotal = (chiTiet) => {
    const total = chiTiet.reduce((sum, item) => {
      return sum + (item.ThanhTien || 0);
    }, 0);
    return total;
  };

  // Calculate final payment after discounts
  const calculateFinalPayment = (values) => {
    const total = calculateOrderTotal(values.ChiTiet || []);
    const ckPhanTram = values.CK_PT || 0;
    const ckTienMat = values.CK_TM || 0;
    
    // Apply percentage discount
    const ckPhanTramValue = (total * ckPhanTram) / 100;
    
    // Note: Individual discount percentages are now handled by names, not calculated here
    // The backend will handle the discount logic based on the person names
    
    // Final payment (basic calculation - backend handles detailed discount logic)
    return total - ckPhanTramValue - ckTienMat;
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Format data for API according to the new required format
      const formattedValues = {
        MaKhoi: values.MaKhoi ? values.MaKhoi.trim() : '',
        MaNhom: values.MaNhom ? values.MaNhom.trim() : '',
        NguoiDat: values.NguoiDat ? values.NguoiDat.trim() : '',
        NguoiNhan: values.NguoiNhan ? values.NguoiNhan.trim() : '',
        SDT: values.SDT ? values.SDT.trim() : '',
        NgayGiao: values.NgayGiao.format("YYYY-MM-DDTHH:mm:ss"),
        CK_PT: values.CK_PT || 0,
        CK_TM: values.CK_TM || 0,
        CK_GioiThieu: values.CK_GioiThieu ? values.CK_GioiThieu.trim() : null,
        CK_ChotDon: values.CK_ChotDon ? values.CK_ChotDon.trim() : null,
        CK_GiaoHang: values.CK_GiaoHang ? values.CK_GiaoHang.trim() : null,
        ChiTiet: (values.ChiTiet || []).filter(item => 
          item && item.MaSP && item.TenSP && item.DonGia && item.SoLuong
        ).map(item => ({
          MaSP: item.MaSP ? item.MaSP.trim() : '',
          TenSP: item.TenSP ? item.TenSP.trim() : '',
          DonGia: parseFloat(item.DonGia) || 0,
          SoLuong: parseInt(item.SoLuong) || 0,
          ThanhTien: parseFloat(item.ThanhTien) || 0
        }))
      };
      
      // Validate that we have at least one valid product
      if (!formattedValues.ChiTiet || formattedValues.ChiTiet.length === 0) {
        message.error("Vui lòng thêm ít nhất một sản phẩm hợp lệ");
        return;
      }
      
      setLoading(true);
      
      console.log("Sending data to API:", formattedValues); // For debugging
      
      const response = await axios.post(
        "https://doticom.vn:3000/api/ThemPhieuDat",
        formattedValues
      );
      
      if (response.data.success) {
        message.success("Thêm phiếu đặt thành công");
        setIsModalVisible(false);
        // Only refresh order data, not full page
        fetchPhieuDat(false);
      } else {
        message.error("Lỗi khi thêm phiếu đặt: " + response.data.error);
      }
    } catch (error) {
      console.error("Submit error:", error);
      message.error("Lỗi khi thêm phiếu đặt: " + (error.message || "Lỗi không xác định"));
    } finally {
      setLoading(false);
    }
  };

  // Submit KPI form
  const handleKpiSubmit = async () => {
    try {
      const values = await kpiForm.validateFields();
      
      // Trim MaKhoi and MaNhom values
      const formattedValues = {
        MaKhoi: values.MaKhoi ? values.MaKhoi.trim() : '',
        MaNhom: values.MaNhom ? values.MaNhom.trim() : '',
        KPI: values.KPI || 0
      };
      
      setKpiLoading(true);
      
      const response = await axios.post(
        "https://doticom.vn:3000/api/SetKPI",
        formattedValues
      );
      
      if (response.data.success) {
        message.success(response.data.message);
        setIsKpiModalVisible(false);
        // Only refresh KPI data, not full page
        fetchKPI(false);
      } else {
        message.error("Lỗi khi thiết lập KPI: " + response.data.error);
      }
    } catch (error) {
      console.error("KPI submit error:", error);
      message.error("Lỗi khi thiết lập KPI: " + (error.message || "Lỗi không xác định"));
    } finally {
      setKpiLoading(false);
    }
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    if (!phieuDatList || phieuDatList.length === 0) {
      return {
        totalOrders: 0,
        totalQuantity: 0,
        totalAmount: 0,
        totalDiscount: 0,
        finalAmount: 0,
      };
    }

    return phieuDatList.reduce((summary, item) => {
      const phieu = item.Phieu;
      const chiTiet = item.ChiTiet;
      
      const totalQuantity = chiTiet.reduce((sum, ct) => sum + ct.SoLuong, 0);
      const totalAmount = chiTiet.reduce((sum, ct) => sum + ct.ThanhTien, 0);
      
      const ckPhanTram = (totalAmount * phieu.CK_PT) / 100;
      const ckTienMat = phieu.CK_TM || 0;
      const ckNoiBo = phieu.CK_PT_NB || 0;
      const ckNoiBoValue = ckNoiBo ? (totalAmount * ckNoiBo) / 100 : 0;
      
      const totalDiscount = ckPhanTram + ckTienMat + ckNoiBoValue;
      const finalAmount = totalAmount - totalDiscount;
      
      return {
        totalOrders: summary.totalOrders + 1,
        totalQuantity: summary.totalQuantity + totalQuantity,
        totalAmount: summary.totalAmount + totalAmount,
        totalDiscount: summary.totalDiscount + totalDiscount,
        finalAmount: summary.finalAmount + finalAmount,
      };
    }, {
      totalOrders: 0,
      totalQuantity: 0,
      totalAmount: 0,
      totalDiscount: 0,
      finalAmount: 0,
    });
  };
  
  const summaryData = calculateSummary();

  // Columns for order list table
  const columns = [
    {
      title: "STT",
      key: "STT",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Người Đặt",
      dataIndex: ["Phieu", "NguoiDat"],
      key: "NguoiDat",
    },
    {
      title: "SĐT",
      dataIndex: ["Phieu", "SDT"],
      key: "SDT",
    },
    {
      title: "Khối",
      dataIndex: ["Phieu", "MaKhoi"],
      key: "MaKhoi",
      render: (value) => value ? value.trim() : '',
    },
    {
      title: "Nhóm",
      dataIndex: ["Phieu", "MaNhom"],
      key: "MaNhom",
      render: (value) => value ? value.trim() : '',
    },
    {
      title: "Ngày Giao",
      dataIndex: ["Phieu", "NgayGiao"],
      key: "NgayGiao",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Tổng SL",
      key: "TongSL",
      render: (_, record) => {
        return record.ChiTiet.reduce((sum, item) => sum + item.SoLuong, 0);
      },
    },
    {
      title: "Tổng Tiền",
      key: "TongTien",
      render: (_, record) => {
        const total = record.ChiTiet.reduce(
          (sum, item) => sum + item.ThanhTien,
          0
        );
        return total.toLocaleString("vi-VN") + " đ";
      },
    },
    {
      title: "CK (%)",
      dataIndex: ["Phieu", "CK_PT"],
      key: "CK_PT",
    },
    {
      title: "CK TM",
      dataIndex: ["Phieu", "CK_TM"],
      key: "CK_TM",
      render: (value) => (value ? value.toLocaleString("vi-VN") + " đ" : "0 đ"),
    },
    {
      title: "Giới thiệu",
      dataIndex: ["Phieu", "CK_GioiThieu"],
      key: "CK_GioiThieu",
      render: (value) => value || "-",
    },
    {
      title: "Chốt đơn",
      dataIndex: ["Phieu", "CK_ChotDon"],
      key: "CK_ChotDon",
      render: (value) => value || "-",
    },
    {
      title: "Giao hàng",
      dataIndex: ["Phieu", "CK_GiaoHang"],
      key: "CK_GiaoHang",
      render: (value) => value || "-",
    },
  ];

  // KPI table columns
  const kpiColumns = [
    {
      title: "STT",
      key: "STT",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Khối",
      dataIndex: "MaKhoi",
      key: "MaKhoi",
      render: (value) => value ? value.trim() : '',
    },
    {
      title: "Nhóm",
      dataIndex: "MaNhom",
      key: "MaNhom",
      render: (value) => value ? value.trim() : '',
    },
    {
      title: "KPI (Số Lượng)",
      dataIndex: "KPI",
      key: "KPI",
      render: (value) => (
        <span style={{ fontWeight: '600', color: 'var(--primary-color)' }}>
          {value.toLocaleString("vi-VN")} cái
        </span>
      ),
    },
  ];

  // Add function to fetch product details
  const fetchProductDetails = async (maSP, fieldIndex) => {
    if (!maSP || maSP.trim() === '') return;
    
    try {
      const response = await axios.get(`https://doticom.vn:3000/items/${maSP.trim()}`);
      
      // Handle the actual API response format - it returns an array directly
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const product = response.data[0]; // Get the first product from the array
        
        // Auto-fill product name and price
        const currentChiTiet = form.getFieldValue('ChiTiet') || [];
        const updatedChiTiet = [...currentChiTiet];
        
        if (updatedChiTiet[fieldIndex]) {
          updatedChiTiet[fieldIndex] = {
            ...updatedChiTiet[fieldIndex],
            TenSP: product.Ten_SP || product.TenSP || product.name || '',
            DonGia: product.DonGia || product.GiaMua || product.price || 0,
          };
          
          // Calculate ThanhTien if SoLuong exists
          const soLuong = updatedChiTiet[fieldIndex].SoLuong || 0;
          const donGia = product.DonGia || product.GiaMua || product.price || 0;
          if (soLuong > 0) {
            updatedChiTiet[fieldIndex].ThanhTien = donGia * soLuong;
          }
        }
        
        form.setFieldsValue({
          ChiTiet: updatedChiTiet
        });
        
        message.success(`Đã tải thông tin sản phẩm: ${product.Ten_SP || product.TenSP || product.name}`);
      } else {
        message.warning(`Không tìm thấy sản phẩm với mã: ${maSP}`);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      if (error.response && error.response.status === 404) {
        message.warning(`Không tìm thấy sản phẩm với mã: ${maSP}`);
      } else {
        message.error(`Lỗi khi tải thông tin sản phẩm: ${error.message}`);
      }
    }
  };

  // Handle product code input with Enter key
  const handleProductCodeKeyPress = (e, fieldIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const maSP = e.target.value;
      fetchProductDetails(maSP, fieldIndex);
    }
  };

  return (
    <div className="phieu-dat-container">
      <Card>
        <div className="header-container">
          <Title level={2}>Quản Lý Phiếu Đặt</Title
          >
          
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              onOpenChange={handleDatePickerOpenChange}
              open={datePickerOpen}
              format="DD/MM/YYYY"
              placeholder={["Từ ngày", "Đến ngày"]}
              allowClear={false}
              style={{ width: '280px' }}
              inputReadOnly
              panelRender={(panel) => (
                <div className="stable-date-panel">
                  {panel}
                </div>
              )}
              renderExtraFooter={() => (
                <div style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <Button 
                    size="small" 
                    onClick={() => setDatePickerOpen(false)}
                  >
                    Đóng
                  </Button>
                </div>
              )}
            />
            <Button 
              type="primary" 
              onClick={fetchPhieuDat}
              icon={<SearchOutlined />}
            >
              Tìm kiếm
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefreshPhieuDat}
              title="Cập nhật dữ liệu"
            >
              Cập nhật
            </Button>
            <Button 
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
            >
              Xuất Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Thêm Phiếu Đặt
            </Button>
          </Space>
        </div>

        {/* Summary Statistics */}
        <div className="summary-section">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Phiếu Đặt"
                  value={summaryData.totalOrders}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: 'var(--primary-color)' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Số Lượng"
                  value={summaryData.totalQuantity}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: 'var(--success-color)' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Tiền"
                  value={summaryData.totalAmount.toLocaleString('vi-VN')}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: 'var(--warning-color)' }}
                  suffix="đ"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Thanh Toán"
                  value={summaryData.finalAmount.toLocaleString('vi-VN')}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: 'var(--danger-color)' }}
                  suffix="đ"
                />
              </Card>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={phieuDatList}
          rowKey={(record) => record.Phieu.IDPhieu}
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} phiếu đặt` 
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div className="expanded-content">
                <div className="expanded-header">
                  <Text strong>Chi tiết đơn hàng:</Text>
                  <Tag color="blue">
                    Ngày tạo: {dayjs(record.Phieu.NgayTao).format("DD/MM/YYYY HH:mm")}
                  </Tag>
                  {/* Display discount persons if available */}
                  <div style={{ marginTop: '8px' }}>
                    {record.Phieu.CK_GioiThieu && (
                      <Tag color="green">Giới thiệu: {record.Phieu.CK_GioiThieu}</Tag>
                    )}
                    {record.Phieu.CK_ChotDon && (
                      <Tag color="orange">Chốt đơn: {record.Phieu.CK_ChotDon}</Tag>
                    )}
                    {record.Phieu.CK_GiaoHang && (
                      <Tag color="purple">Giao hàng: {record.Phieu.CK_GiaoHang}</Tag>
                    )}
                  </div>
                </div>
                <Table
                  columns={[
                    { title: "Mã SP", dataIndex: "MaSP", key: "MaSP" },
                    { title: "Tên SP", dataIndex: "TenSP", key: "TenSP" },
                    { 
                      title: "Đơn Giá", 
                      dataIndex: "DonGia", 
                      key: "DonGia",
                      render: (value) => (
                        <span className="currency-value">
                          {value.toLocaleString("vi-VN")} đ
                        </span>
                      )
                    },
                    { title: "Số Lượng", dataIndex: "SoLuong", key: "SoLuong" },
                    { 
                      title: "Thành Tiền", 
                      dataIndex: "ThanhTien", 
                      key: "ThanhTien",
                      render: (value) => (
                        <span className="currency-value">
                          {value.toLocaleString("vi-VN")} đ
                        </span>
                      )
                    },
                  ]}
                  dataSource={record.ChiTiet}
                  pagination={false}
                  rowKey="MaSP"
                  summary={pageData => {
                    const total = pageData.reduce((sum, item) => sum + item.ThanhTien, 0);
                    
                    return (
                      <>
                        <Table.Summary.Row className="total-row">
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <Text strong>Tổng cộng</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <Text strong className="currency-value">
                              {total.toLocaleString("vi-VN")} đ
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                />
              </div>
            ),
          }}
        />
      </Card>

      {/* KPI Management Table */}
      <Card style={{ marginTop: '24px' }}>
        <div className="header-container">
          <Title level={3}>Quản Lý KPI</Title
          >
          
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefreshKPI}
              title="Cập nhật dữ liệu KPI"
            >
              Cập nhật
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showKpiModal}
            >
              Thêm KPI
            </Button>
          </Space>
        </div>

        <Table
          columns={kpiColumns}
          dataSource={kpiList}
          rowKey={(record) => `${record.MaKhoi}-${record.MaNhom}`}
          loading={kpiLoading}
          pagination={{ 
            pageSize: 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} mục KPI` 
          }}
        />
      </Card>

      {/* Modal thêm phiếu đặt */}
      <Modal
        title="Thêm Phiếu Đặt Mới"
        open={isModalVisible}
        onCancel={handleCancel}
        width={1000}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            Lưu
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            NgayGiao: dayjs(),
            CK_PT: 0,
            CK_TM: 0,
            CK_GioiThieu: "",
            CK_ChotDon: "",
            CK_GiaoHang: "",
            ChiTiet: [{}],
          }}
        >
          {/* Phần 1: Thông tin khách hàng */}
          <Card title="Thông Tin Khách Hàng" className="form-card">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="NguoiDat"
                  label="Tên Khách Hàng"
                  rules={[{ required: true, message: "Vui lòng nhập tên khách hàng" }]}
                >
                  <Input 
                    placeholder="Nhập tên khách hàng" 
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="SDT"
                  label="Số Điện Thoại"
                  rules={[{ required: true, message: "Vui lòng nhập số điện thoại" }]}
                >
                  <Input 
                    placeholder="Nhập số điện thoại" 
                    prefix={<i className="fas fa-phone" style={{ color: 'rgba(0,0,0,.25)' }}></i>}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="MaKhoi"
                  label="Khối"
                  rules={[{ required: true, message: "Vui lòng chọn khối" }]}
                >
                  <Select placeholder="Chọn khối">
                    <Option value="VP">VP</Option>
                    <Option value="XSX">XSX</Option>
                    <Option value="DTSG">DTSG</Option>
                    <Option value="Bonpas">Bonpas</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="MaNhom"
                  label="Nhóm"
                  rules={[{ required: true, message: "Vui lòng chọn nhóm" }]}
                >
                  <Select placeholder="Chọn nhóm">
                    <Option value="Nhom1">Nhóm 1</Option>
                    <Option value="Nhom2">Nhóm 2</Option>
                    <Option value="Nhom3">Nhóm 3</Option>
                    <Option value="Nhom4">Nhóm 4</Option>
                    <Option value="Nhom5">Nhóm 5</Option>
                    <Option value="Main">Bonpas/Bakery</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="NguoiNhan"
                  label="Người Nhận"
                  rules={[{ required: true, message: "Vui lòng nhập người nhận" }]}
                >
                  <Input 
                    placeholder="Nhập người nhận" 
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="NgayGiao"
                  label="Ngày Giao"
                  rules={[{ required: true, message: "Vui lòng chọn ngày giao" }]}
                >
                  <DatePicker 
                    style={{ width: "100%" }} 
                    format="DD/MM/YYYY"
                    placeholder="Chọn ngày giao"
                    suffixIcon={<CalendarOutlined />}
                    disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
                    inputReadOnly
                    onChange={() => {
                      // Auto close when date is selected
                      setTimeout(() => {
                        const pickerElement = document.querySelector('.ant-picker-dropdown');
                        if (pickerElement) {
                          pickerElement.style.display = 'none';
                        }
                      }, 100);
                    }}
                    panelRender={(panel) => (
                      <div className="stable-date-panel">
                        {panel}
                      </div>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Phần 2: Chi tiết đơn hàng */}
          <Card title="Chi Tiết Đơn Hàng" className="form-card">
            <Form.List name="ChiTiet">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Row gutter={16} key={field.key} className="ant-form-list-item fade-in">
                      <Col span={5}>
                        <Form.Item
                          {...field}
                          name={[field.name, "MaSP"]}
                          fieldKey={[field.fieldKey, "MaSP"]}
                          rules={[{ required: true, message: "Nhập mã sản phẩm" }]}
                        >
                          <Input 
                            placeholder="Mã sản phẩm" 
                            onKeyPress={(e) => handleProductCodeKeyPress(e, field.name)}
                            onBlur={(e) => {
                              // Also trigger on blur for better UX
                              const maSP = e.target.value;
                              if (maSP && maSP.trim() !== '') {
                                fetchProductDetails(maSP, field.name);
                              }
                            }}
                            style={{ 
                              borderColor: 'var(--primary-color)',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...field}
                          name={[field.name, "TenSP"]}
                          fieldKey={[field.fieldKey, "TenSP"]}
                        >
                          <Input 
                            placeholder="Tên sản phẩm" 
                            style={{ 
                              backgroundColor: 'var(--gray-50)',
                              fontWeight: '500'
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...field}
                          name={[field.name, "DonGia"]}
                          fieldKey={[field.fieldKey, "DonGia"]}
                          rules={[{ required: true, message: "Nhập đơn giá" }]}
                        >
                          <InputNumber
                            style={{ 
                              width: "100%",
                              backgroundColor: 'var(--gray-50)',
                              fontWeight: '500'
                            }}
                            placeholder="Đơn giá"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                            onChange={(value) => {
                              // Auto calculate ThanhTien
                              const soLuong = form.getFieldValue(["ChiTiet", field.name, "SoLuong"]) || 0;
                              const currentChiTiet = form.getFieldValue('ChiTiet') || [];
                              const updatedChiTiet = [...currentChiTiet];
                              
                              if (updatedChiTiet[field.name]) {
                                updatedChiTiet[field.name] = {
                                  ...updatedChiTiet[field.name],
                                  ThanhTien: (value || 0) * soLuong,
                                };
                              }
                              
                              form.setFieldsValue({
                                ChiTiet: updatedChiTiet
                              });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item
                          {...field}
                          name={[field.name, "SoLuong"]}
                          fieldKey={[field.fieldKey, "SoLuong"]}
                          rules={[{ required: true, message: "Nhập số lượng" }]}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Số lượng"
                            min={1}
                            onChange={(value) => {
                              // Auto calculate ThanhTien
                              const donGia = form.getFieldValue(["ChiTiet", field.name, "DonGia"]) || 0;
                              const currentChiTiet = form.getFieldValue('ChiTiet') || [];
                              const updatedChiTiet = [...currentChiTiet];
                              
                              if (updatedChiTiet[field.name]) {
                                updatedChiTiet[field.name] = {
                                  ...updatedChiTiet[field.name],
                                  ThanhTien: (value || 0) * donGia,
                                };
                              }
                              
                              form.setFieldsValue({
                                ChiTiet: updatedChiTiet
                              });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...field}
                          name={[field.name, "ThanhTien"]}
                          fieldKey={[field.fieldKey, "ThanhTien"]}
                        >
                          <InputNumber
                            style={{ 
                              width: "100%",
                              backgroundColor: 'var(--gray-50)',
                              fontWeight: '600',
                              color: 'var(--success-color)'
                            }}
                            placeholder="Thành tiền"
                            disabled
                            className="currency-value"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={1}>
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          className="delete-button"
                        />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      className="add-item-button"
                    >
                      Thêm Sản Phẩm
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>

          {/* Phần 3: Chiết khấu */}
          <Card title="Chiết Khấu" className="form-card">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="CK_PT"
                  label="Chiết Khấu (%)"
                  initialValue={0}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    placeholder="Nhập % chiết khấu"
                    prefix="%"
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="CK_TM"
                  label="Chiết Khấu Tiền Mặt"
                  initialValue={0}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    placeholder="Nhập chiết khấu tiền mặt"
                    prefix={<DollarOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="CK_GioiThieu"
                  label="Người Giới Thiệu"
                >
                  <Input
                    placeholder="Nhập tên người giới thiệu"
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="CK_ChotDon"
                  label="Người Chốt Đơn"
                >
                  <Input
                    placeholder="Nhập tên người chốt đơn"
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="CK_GiaoHang"
                  label="Người Giao Hàng"
                >
                  <Input
                    placeholder="Nhập tên người giao hàng"
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Modal>

      {/* Modal thêm KPI */}
      <Modal
        title="Thiết Lập KPI"
        open={isKpiModalVisible}
        onCancel={handleKpiCancel}
        width={600}
        footer={[
          <Button key="back" onClick={handleKpiCancel}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={kpiLoading}
            onClick={handleKpiSubmit}
          >
            Lưu
          </Button>,
        ]}
      >
        <Form
          form={kpiForm}
          layout="vertical"
          initialValues={{
            KPI: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="MaKhoi"
                label="Khối"
                rules={[{ required: true, message: "Vui lòng chọn khối" }]}
              >
                <Select placeholder="Chọn khối">
                  <Option value="VP">VP</Option>
                  <Option value="XSX">XSX</Option>
                  <Option value="DTSG">DTSG</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="MaNhom"
                label="Nhóm"
                rules={[{ required: true, message: "Vui lòng chọn nhóm" }]}
              >
                <Select placeholder="Chọn nhóm">
                  <Option value="Nhom1">Nhóm 1</Option>
                  <Option value="Nhom2">Nhóm 2</Option>
                  <Option value="Nhom3">Nhóm 3</Option>
                  <Option value="Nhom4">Nhóm 4</Option>
                  <Option value="Nhom5">Nhóm 5</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="KPI"
                label="KPI Số Lượng Bánh"
                rules={[{ required: true, message: "Vui lòng nhập KPI số lượng bánh" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Nhập KPI số lượng bánh"
                  addonAfter="cái"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PhieuDat;
